import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Profile } from "@/types/profile";
import { Images, User, Play, ChevronRight } from "lucide-react";

interface ProfileBalloonProps {
  profile: Profile;
  onViewPhotos: () => void;
  onViewProfile: () => void;
  onWatchVideo: () => void;
  isShaking?: boolean;
}

const ProfileBalloon = memo(({
  profile,
  onViewPhotos,
  onViewProfile,
  onWatchVideo,
  isShaking = false,
}: ProfileBalloonProps) => {
  const { t } = useTranslation();

  return (
    <div 
      className={`relative flex flex-col items-center animate-float ${isShaking ? "animate-shake" : ""}`}
    >
      {/* Main Glass Balloon Container */}
      <div className="relative">
        {/* Outer glow */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-sky-200/40 to-pink-200/40 blur-2xl scale-110" />
        
        {/* Glass balloon */}
        <div className="relative w-72 h-72 rounded-full glass-container flex items-center justify-center overflow-hidden">
          {/* Inner gradient glow */}
          <div className="absolute inset-2 rounded-full bg-gradient-to-br from-white/30 via-transparent to-pink-100/20" />
          
          {/* Bubble reflections */}
          <div className="absolute top-6 left-8 w-12 h-12 rounded-full bg-white/40 blur-sm" />
          <div className="absolute top-10 left-16 w-6 h-6 rounded-full bg-white/30" />
          <div className="absolute bottom-20 right-8 w-4 h-4 rounded-full bg-white/25 blur-sm" />
          
          {/* Profile Photo - larger to fill more of balloon */}
          <div className="relative w-56 h-56 rounded-full overflow-hidden shadow-lg ring-4 ring-white/50">
            <img
              src={profile.photos[0]}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Balloon tie/knot */}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[16px] border-l-transparent border-r-transparent border-t-white/40" />
      </div>
      
      {/* Action Buttons Row - styled like reference */}
      <div className="flex items-center gap-2 mt-4">
        <button
          onClick={onViewPhotos}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium text-sm shadow-lg hover:scale-105 transition-transform"
        >
          <Images className="w-4 h-4" />
          {t("popBalloon.viewPhotos")}
        </button>
        
        <button
          onClick={onViewProfile}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium text-sm shadow-lg hover:scale-105 transition-transform"
        >
          <User className="w-4 h-4" />
          {t("popBalloon.viewProfile")}
          <ChevronRight className="w-4 h-4 -mr-1" />
        </button>
        
        {profile.hasVideo && (
          <button
            onClick={onWatchVideo}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium text-sm shadow-lg hover:scale-105 transition-transform"
          >
            <Play className="w-4 h-4" />
            {t("popBalloon.watchVideo")}
            <ChevronRight className="w-4 h-4 -mr-1" />
          </button>
        )}
      </div>
    </div>
  );
});

ProfileBalloon.displayName = "ProfileBalloon";

export default ProfileBalloon;
